import { arrayMove } from '@dnd-kit/sortable';
import { useCallback } from 'react';

import type { ApiFilestackImageOrVideoDataWithCaption } from 'api/types/ApiFilestackImageData';
import { Box } from 'components/Box/Box';
import { Icon } from 'components/Icon/Icon';
import { BodySmall } from 'components/Text/BodySmall';
import { useI18n } from 'i18n/hooks/useI18n';

import { AddButton } from './components/AddButton';
import { SortableGrid } from './components/SortableGrid';
import { useMultiImagePickerAddImage } from './useMultiImagePickerAddImage';
import { useMultiImagePickerEditImage } from './useMultiImagePickerEditImage';
import { useMultiImagePickerRemoveImage } from './useMultiImagePickerRemoveImage';

type Props = {
  width?: number;
  height?: number;
  altTextRequired?: boolean;
  values: ApiFilestackImageOrVideoDataWithCaption[] | null | undefined;
  onChange: (arg0: ApiFilestackImageOrVideoDataWithCaption[]) => void;
  allowSVG?: boolean;
  allowVideo?: boolean;
  qaId: string;
  extraAnalyticsData?: Record<string, unknown>;
};

export function MultiImagePicker({
  width,
  height,
  altTextRequired,
  values,
  onChange,
  allowSVG,
  allowVideo,
  qaId,
  extraAnalyticsData,
}: Props) {
  const { i18n } = useI18n();

  const sortHandler = useCallback(
    ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
      onChange(arrayMove(values ?? [], oldIndex, newIndex));
    },
    [values, onChange],
  );

  const { addImage } = useMultiImagePickerAddImage({
    width,
    height,
    allowSVG,
    allowVideo,
    values,
    extraAnalyticsData,
    altTextRequired,
    onChange,
  });

  const { editImage } = useMultiImagePickerEditImage({
    allowSVG,
    allowVideo,
    values,
    onChange,
    extraAnalyticsData,
    altTextRequired,
  });

  const { removeImage } = useMultiImagePickerRemoveImage({
    values,
    onChange,
  });

  return (
    <Box qaId={qaId}>
      <SortableGrid
        assets={values ?? []}
        removeImage={removeImage}
        editImage={editImage}
        qaId={qaId}
        onSortEnd={sortHandler}
      >
        <AddButton
          qaId={`${qaId}-add-image`}
          onClick={addImage}
          borderRadius="4px"
          p={10}
          m={11}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            width={130}
            height={130}
          >
            <Box mt={16}>
              <Icon name="upload" size={23} />
            </Box>

            <BodySmall>
              {allowVideo ? i18n('Add Image or Video') : i18n('Add Image')}
            </BodySmall>
          </Box>
        </AddButton>
      </SortableGrid>
    </Box>
  );
}
